import React, { useEffect } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import slugify from '../../helpers/slugify';
import Errors from '../common/Errors';
import Row from '../common/Row';
import TimeStamp from '../common/TimeStamp';
import {
  handleDestroyContainer,
  handleEditContainer,
} from './constants/handleContainer';
import { confirmation } from '../../helpers/notifications';
import Icon from '../common/Icon';
import { useNavigate } from 'react-router-dom';

const ContainerEdit = ({
  container,
  toggleEdit,
  setContainer,
  reloadContainerData,
}) => {
  const { id, created_at, updated_at, ...editableFields } = container;
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues: { ...editableFields },
    criteriaMode: 'all',
  });
  const { isDirty, dirtyFields } = useFormState({ control });
  const navigate = useNavigate();

  const onSubmit = async (formData) => {
    if (isDirty) {
      let editedContainer = await handleEditContainer(formData, id);
      if (editedContainer.ok) {
        if (!editableFields['archived'] && editedContainer.data.archived) {
          // reloads forms to reflect 'archived' status if container saved as archived
          reloadContainerData();
        }
        setContainer(editedContainer.data);
        toggleEdit();
      } else if (editedContainer.errors) {
        Object.entries(editedContainer.errors).forEach(([key, message]) =>
          setError(key, { types: { [key]: message } }),
        );
      }
    } else {
      toggleEdit();
    }
  };

  const handleContainerDelete = async (e) => {
    e.preventDefault();
    await handleDestroyContainer(container, (response) => {
      if (response.ok) navigate('/manage');
    });
  };

  return (
    <>
      <div className='details'>
        <div className='controls'>
          <Icon
            icon='xmark'
            onClick={(e) => toggleEdit()}
            title='Cancel Edit'
          />
        </div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className='controls lower'>
            <button className='danger' onClick={handleContainerDelete}>
              Delete
              <Icon icon='trash' />
            </button>
            <button type='submit'>
              Save Container
              <Icon icon='save' />
            </button>
            <button
              className='cancel blue'
              onClick={(e) => {
                e.preventDefault();
                toggleEdit();
              }}
            >
              Cancel
              <Icon icon='xmark' />
            </button>
          </div>
          <div style={{ marginTop: '20px' }} />
          <Row name='Display Name:'>
            <input {...register('display_name')} />
          </Row>
          <Row name='Name:'>
            <input {...register('name', { required: 'Required' })} />
            <ErrorMessage
              errors={errors}
              name='name'
              render={({ messages }) => <Errors messages={messages} />}
            />
          </Row>
          <Row name='Permalink:'>
            <input
              {...register('slug', {
                required: 'Required',
                onBlur: (e) =>
                  setValue('slug', slugify(e.target.value), {
                    shouldDirty: true,
                  }),
              })}
            />
            <ErrorMessage
              errors={errors}
              name='slug'
              render={({ messages }) => <Errors messages={messages} />}
            />
          </Row>
          <Row
            name='Active:'
            className={watch('archived') ? 'row-disabled' : ''}
          >
            <input
              type='checkbox'
              id='container-active'
              {...register('active', {
                disabled: watch('archived'),
              })}
            />
            <label htmlFor='container-active'></label>
          </Row>
          <Row name='Archived:'>
            <input
              type='checkbox'
              id='container-archived'
              {...register('archived', {
                onChange: (e) => {
                  if (e.target.checked) {
                    confirmation.acknowledge(
                      () => {
                        setValue('active', false);
                      },
                      () => {
                        setValue('archived', false);
                      },
                      'Notice',
                      'All forms in this container will be archived automatically.',
                      'Ok',
                      'Cancel',
                    );
                  }
                },
              })}
            />
            <label htmlFor='container-archived'></label>
          </Row>

          <TimeStamp title='Created' value={created_at} />
          <TimeStamp title='Updated' value={updated_at} />
        </form>
      </div>
    </>
  );
};

export default ContainerEdit;
